import React, { useState } from "react";
import * as i18n from "util/i18n";
import { Col, Row } from "react-bootstrap";
import MainContainer from "pages/_components/MainContainer";
import Button from "pages/_components/Button";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import { ResponsiveHeader } from "pages/_components/header";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import Notification from "pages/_components/Notification";
import { actions as echeckActions } from "reducers/echecks";
import { useTransaction } from "hooks/useTransactions";
import { useDispatch } from "react-redux";
import { bool } from "prop-types";
import TransactionSummaryBody from "../EcheckActions/components/TransactionSummaryBody";
import EndorseTransactionMultipleBody from "./components/TransactionMultipleBody"
import { isFinishedWithErrors } from "../utils"
import { useLocation } from "react-router-dom";

const EndorseEcheckTransactionPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const formatTransactionEcheck = (data, idTransaction) => {
        const formData = {
            type: data.type,
            reference: data.reference,
            referencevalue: data.referencevalue,
            beneficiary: { document: data.beneficiary, reference: data.beneficiaryName },
            idTransaction,
            reason: data.specialAttribute,
        };
        dispatch(echeckActions.setEchecksToDoActions({ action: data.action, echeqList: data.echeqList }));

        return { ...formData };
    };

    const { pathname } = useLocation();
    const action = pathname?.split("/")[3];
  
    const { isLoading, transaction, handleSignTransaction, showSignButton, push, id, userId, status } = useTransaction(
       `/echecks/action/${action}/confirm`,
        echeckActions.endorseEcheqData,
        echeckActions.cleanEcheckTicket,
        formatTransactionEcheck,
    );

    const { componentRef, handlePrint, handlePrintMobile, mobileRef, handleShareMobile } = useReactPrint(
        `echecks.${action}`,
        id,
        "echecks/endorse",
    );

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const { isBackoffice, isFinished, isPending, isCancelled, isFailed, isFlowComplete } = status;

    const [showMoreData, setShowMoreData] = useState(!isDesktop);

    const cancelTransaction = () => {
        const { data, idTransaction } = transaction;
        const echeqData = {
            action: data.action,
            echeqList: data.echeqList,
            type: data.type,
            reference: data.reference,
            referencevalue: data.referencevalue,
            beneficiary: {
                document: data.beneficiary,
                reference: data.beneficiaryName,
            },
            idTransaction,
            cancelTransaction: true,
            userId,
            reason: data.specialAttribute,
        };

        dispatch(echeckActions.endorseEcheqData(echeqData));
        dispatch(echeckActions.setEchecksToDoActions(echeqData));
        dispatch(push( `/echecks/action/${action}/confirm`));
    };

    const renderBackToHomeButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={() => dispatch(push("/desktop"))}
                label="forms.transaction.ticket.actions.backToHome"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderDownloadButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleDownload}
                image="images/figma/icon/download.svg"
                label="global.download"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderCancelButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={cancelTransaction}
                label="lotPayment.cancel"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderSignButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleSignTransaction}
                label="forms.signTransaction.link"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const getTitle = () => {
        if (isCancelEndorse)
            return "echecks.multiple.cancelEndorse";

        return transaction?.data?.echeqList?.length > 1 ? "echecks.multiple.endorse" : "echecks.endorse";
    }

    const backLinkTo = isFlowComplete ? "/echecks" : "";
    const withErrorsState = isFinishedWithErrors(transaction?.data?.endorsements) ? "FINISHED_WITH_ERRORS" : null;
    const subTitle = withErrorsState ? "ticket.status." + withErrorsState : null;
    const isCancelEndorse = transaction?.idActivity == "cancel.endorse.echeq";
    const isMultipleEcheq = transaction?.data?.echeqList?.length > 1;

    return (
        <>
            {isFinished && <Notification scopeToShow="echecks/endorse" />}
            {!isBackoffice && <ResponsiveHeader ticket id={getTitle()} metaTitle={getTitle()} backLinkTo={backLinkTo} />}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction} >
                            <TransactionHeader title={getTitle()} status={withErrorsState} subTitle={subTitle}/>
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    {isMultipleEcheq && (
                                        <EndorseTransactionMultipleBody
                                            setShowMoreData={setShowMoreData} 
                                            showMoreData={showMoreData} 
                                            isDesktop={isDesktop}
                                            isCancelEndorse={isCancelEndorse}
                                        />
                                    )}
                                    {!isMultipleEcheq && (
                                        <TransactionSummaryBody
                                            setShowMoreData={setShowMoreData}
                                            showMoreData={showMoreData}
                                            isDesktop={isDesktop}
                                        />
                                    )}

                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            {(isFinished  || isCancelled ||
                                isFailed) && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderDownloadButton(true, 0)}
                                </>
                            )}{" "}
                            {isPending &&
                                (showSignButton ? (
                                    <>
                                        {renderCancelButton(false, 2)}
                                        {renderSignButton(true, 0)}
                                    </>
                                ) : (
                                    <>
                                        {renderBackToHomeButton(false, 2)}
                                        {renderCancelButton(true, 0)}
                                    </>
                                ))}
                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

EndorseEcheckTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default EndorseEcheckTransactionPage;